import { AxiosResponse } from 'axios'
import {
  CaseTypeServiceMappingPayload,
  DataJson,
  DataTypeStore,
  DuplicateFormValues,
  EntityPermissionType,
  ExportPayload,
  formatDataObject,
  getEntityDataByEntityFilter,
  GetEntityDataByNameIdFilter,
  GetGenericEntityListingApiType,
  HCPCopayConfigurationResponse,
  IEntityType,
  IntegrationType,
  ObjectType,
  PayloadEntityId,
  SavedConfigTypeAPI,
} from '../types'
import axiosInstance from '../utils/axiosInstance'
import { API_END_POINT, ENTITY_MANAGER_END_POINTS } from '../utils/environment'
import {
  EntityLookupSearchCriteria,
  SearchCriteria,
  TableFilterApiBody,
} from '../types/tableColumn.types'
import { getStringFilterOptionsEntityManager } from './stringFilter.api'
import { isValidEntityDataId, validateApiParams } from '../utils/functions'
import { buildEntitiesFilterUrl } from '../utils/functions/networkError.utils'
import { getEntityNameById, resolveEntityName } from '../utils/entities.utils'
import { FieldValues } from 'react-hook-form'

const ENTITY_ENDPOINT = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data`

const EMPTY_SUB_RESOURCE_PAGE = {
  data: { content: [], totalElements: 0, totalPages: 0, size: 0, number: 0 },
}

const searchURL = (customFilter?: boolean) => {
  if (customFilter) {
    return `${API_END_POINT.ENTITY_MANAGER}entities/fetch`
  }
  return `${API_END_POINT.ENTITY_MANAGER}entities/filter-data/`
}

export const getEntityById = async (payload: { entityId: number }) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER}entities/${payload?.entityId}`
  validateApiParams(
    { entityId: payload?.entityId },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint)
}

export const getEntityDataById = async (payload: {
  entityId: number
  entity_name: string
  keys: string
}) => {
  const resolvedEntityName = resolveEntityName(
    payload.entity_name,
    payload.entityId
  )
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/${resolvedEntityName}/${payload?.entityId}?embedded=${payload.keys}&entityName=organizations`
  validateApiParams(
    { entity_name: resolvedEntityName, entityId: payload?.entityId },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint)
}

export const getEntityDataByEntityIdLayoutIdApi = async (
  payload: PayloadEntityId
) => {
  const resolvedEntityName = resolveEntityName(payload.name, payload.id)
  if (payload.page === undefined || payload.size === undefined) {
    const endpoint = `${ENTITY_ENDPOINT}/${resolvedEntityName}?entityId=${payload.id}&layoutId=${payload.layoutId}`
    validateApiParams(
      {
        name: resolvedEntityName,
        entityId: payload.id,
        layoutId: payload.layoutId,
      },
      {
        endpoint,
        method: 'GET',
      }
    )
    return axiosInstance.get(endpoint)
  }
  const endpoint = `${ENTITY_ENDPOINT}/${resolvedEntityName}/paginated?entityId=${payload.id}&layoutId=${payload.layoutId}&page=${payload.page}&size=${payload.size}`
  validateApiParams(
    {
      name: resolvedEntityName,
      entityId: payload.id,
      layoutId: payload.layoutId,
    },
    {
      endpoint,
      method: 'GET',
    }
  )
  return axiosInstance.get(endpoint)
}

export const getEntityDataByEntityIdApi = async (payload: {
  id: string
  name: string
}) => {
  const resolvedEntityName = resolveEntityName(payload.name, payload.id)
  const endpoint = `${ENTITY_ENDPOINT}/${resolvedEntityName}?entityId=${payload.id}`
  validateApiParams(
    { name: resolvedEntityName, entityId: payload.id },
    {
      endpoint,
      method: 'GET',
    }
  )
  return axiosInstance.get(endpoint)
}

export const getBaseEntities = async () => {
  return await axiosInstance.get(`${API_END_POINT.ENTITY_MANAGER}base/entities`)
}

export const createEntity = async <T>(payload: T) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER}entities`,
    payload
  )
}

export const createField = async <T extends { entityId: string; data: U }, U>(
  payload: T
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER}entities/fields/${payload.entityId}`
  validateApiParams(
    { entityId: payload.entityId },
    {
      endpoint,
      method: 'POST',
    }
  )
  return axiosInstance.post(endpoint, payload.data)
}

export const updateEntity = async <T extends { entityId: number; data: U }, U>(
  payload: T
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER}entities/${payload.entityId}`
  validateApiParams(
    { entityId: payload.entityId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return axiosInstance.put(endpoint, payload.data)
}

export const updateField = async <T>(payload: {
  id: number
  entityId: string
  data: T
}) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER}entities/fields/${payload.id}?entityId=${payload.entityId}`
  validateApiParams(
    { id: payload.id, entityId: payload.entityId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return axiosInstance.put(endpoint, payload.data)
}

export const createRelation = async <T>(payload: T) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER}entities/relation`,
    payload
  )
}

export const updateRelation = async <T>(payload: { id: number; data: T }) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER}entities/relation/${payload.id}`
  validateApiParams(
    { id: payload.id },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(endpoint, payload.data)
}

export const getAllEntities = async (
  extendsType?: string
): Promise<AxiosResponse<IEntityType[]> | undefined> => {
  return await axiosInstance.get(
    `${
      API_END_POINT.ENTITY_MANAGER
    }entities?includeSourceTypes=GLOBAL,SYSTEM,USER${
      extendsType ? `&excludeTypes=${extendsType}` : ''
    }`,
    {}
  )
}

export const getCategories = async (categoryValue?: string) => {
  return await axiosInstance.post(
    `${API_END_POINT?.ENTITY_MANAGER_V3}entity-data/filters/category_values`,
    {
      entity_name: 'category_values',
      select: ['category'],
      distinct: true,
      filters: [
        {
          criteria: [
            {
              field_name: categoryValue ? categoryValue : 'category',
            },
          ],
        },
      ],
    }
  )
}

export const getFieldValues = async (
  entityName: string,
  field_name?: string,
  filters?: {
    [key: string]: string | undefined
  }
) => {
  const resolvedEntityName = resolveEntityName(entityName, undefined)
  validateApiParams(
    {
      entity_name: resolvedEntityName,
    },
    {
      endpoint: `${API_END_POINT?.ENTITY_MANAGER_V3}entity-data/filters/${resolvedEntityName}`,
      method: 'POST',
    }
  )
  return await axiosInstance.post(
    `${API_END_POINT?.ENTITY_MANAGER_V3}entity-data/filters/${resolvedEntityName}`,
    {
      entity_name: resolvedEntityName,
      select: [field_name],
      distinct: true,
      filters: [
        {
          criteria: [
            ...(filters
              ? Object.keys(filters)
                  .filter((key) => filters[key])
                  .map((key) => ({
                    values: [filters[key]],
                    operation: 'EQUALS',
                    field_name: key,
                  }))
              : []),
          ],
        },
      ],
    }
  )
}

export const getAllEntitiesPaginated = async (payload: {
  page: number
  size: string
  entityId?: number
  status?: boolean
  bodyPayload?: DataJson<string | number | null, string | boolean> | null
}) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER}entities/filters?page=${
      payload?.page - 1
    }&size=${payload?.size}`,
    payload?.bodyPayload ? payload?.bodyPayload : {}
  )
}

export const getPaginatedEntitiesByType = async (payload: {
  page: number
  size: string
  entityId?: number
  status?: boolean
  type?: string
  bodyPayload?: DataJson<string | number | null, string | boolean> | null
}) => {
  // RUM 005: previously used `$type=` (missing `&`) → malformed query / HTTP 400
  const url = buildEntitiesFilterUrl(
    `${API_END_POINT?.ENTITY_MANAGER}entities/filters`,
    payload?.page - 1,
    payload?.size,
    payload?.type
  )
  return await axiosInstance.post(
    url,
    payload?.bodyPayload ? payload?.bodyPayload : {}
  )
}

export const fetchFilteredEntityList = async (payload: {
  page: number
  size: string
  filter?: TableFilterApiBody
  sort?: string
  includeSourceTypes?: string
}) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER}entities/filters?sort=enabled,DESC`,

    payload?.filter,
    {
      params: {
        page: payload?.page - 1,
        size: payload?.size,
        includeSourceTypes: payload?.includeSourceTypes,
        sort: payload?.sort,
      },
    }
  )
}

export const getColumnFilterOptions = async (
  searchCriteria: SearchCriteria,
  currentTabIndex?: string | number | undefined
) => {
  const entitySearchType =
    currentTabIndex === '1' ? ['USER', 'GLOBAL'] : ['SYSTEM']
  const searchPayload = [
    searchCriteria,
    {
      search_key: 'SOURCE_TYPE',
      operator: 'IN',
      search_value: [...entitySearchType],
    },
  ]
  return getStringFilterOptionsEntityManager(searchURL(), searchPayload)
}

export const getGenericEntityListingAPI = async (
  payload: GetGenericEntityListingApiType,
  recordId?: string | number
) => {
  const resolvedEntityName = resolveEntityName(
    payload.entity_name,
    payload.entity_id
  )
  const url = recordId
    ? `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/${resolvedEntityName}/${recordId}`
    : `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/${resolvedEntityName}`
  validateApiParams(
    { entity_name: resolvedEntityName },
    { endpoint: url, method: 'POST' }
  )
  try {
    const requestPayload = {
      ...payload,
      entity_name: resolvedEntityName,
    }
    const res = await axiosInstance.post(url, requestPayload)
    if (res) return res.data
  } catch (err) {
    console.error(err)
  }
  return []
}

export const deleteEntityByIdApi = async <T>(entityId: T) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER}entities/${entityId}`
  return axiosInstance.delete(endpoint)
}

export const toggleEntityByIdApi = async <T>(entityId: T, status: T) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER}entities/${entityId}`
  return axiosInstance.patch(endpoint, {
    enabled: `${status}`,
  })
}

export const getBookmarkedEntites = async () => {
  return await axiosInstance.get(
    `${API_END_POINT.ENTITY_MANAGER}entities/bookmark`
  )
}

export const postBookmarkedEntites = async (
  payload: {
    id: string
    name: string
    singular_label: string
    plural_label: string
  }[]
) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER}entities/bookmark`,
    payload
  )
}

export const deleteFieldByIdApi = async (payload: {
  entityId: string
  fieldId: string
}) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER}entities/fields/${payload.fieldId}?entityId=${payload.entityId}`
  validateApiParams(
    { entityId: payload.entityId, fieldId: payload.fieldId },
    {
      endpoint,
      method: 'DELETE',
    }
  )
  return axiosInstance.delete(endpoint)
}

export const getSubResources = async (
  entityName: string,
  entityId: string,
  layoutId: string,
  relationIdentifier: string,
  unassigned: boolean,
  dataId: string,
  page?: number,
  rowsPerPage?: number,
  embedded?: string,
  filters?: { logical_operator?: string; criteria: formatDataObject[] }[],
  cols?: string[],
  related_entities?: Record<
    string,
    {
      select: string[]
    }
  >,
  function_parameters?: FieldValues
) => {
  if (!isValidEntityDataId(dataId)) {
    return Promise.resolve(EMPTY_SUB_RESOURCE_PAGE)
  }

  const resolvedEntityName = resolveEntityName(entityName, entityId)
  if (embedded) {
    const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${entityId}/${dataId}/entities/paginated?entityName=${resolvedEntityName}&relationIdentifier=${relationIdentifier}&unassigned=${unassigned}&embedded=${embedded}`
    // RUM 005: entityId in path must be present
    if (!isValidEntityDataId(entityId)) {
      return Promise.resolve(EMPTY_SUB_RESOURCE_PAGE)
    }
    validateApiParams(
      { resolvedEntityName, dataId, entityId },
      {
        endpoint,
        method: 'GET',
      }
    )
    return await axiosInstance.get(endpoint)
  } else if (filters) {
    const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/get/${resolvedEntityName}/${dataId}/entities?unassigned=${unassigned}`
    validateApiParams(
      { resolvedEntityName, dataId },
      {
        endpoint,
        method: 'POST',
      }
    )
    return await axiosInstance.post(endpoint, {
      entity_id: entityId,
      layout_id: layoutId,
      unassigned,
      relation_identifier: relationIdentifier,
      page,
      size: rowsPerPage,
      filters,
      select: cols ?? [],
      related_entities: related_entities,
      ...(function_parameters ? { function_parameters } : {}),
    })
  } else if (page === undefined || rowsPerPage === undefined) {
    const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${resolvedEntityName}/${dataId}/entities?entityId=${entityId}&layoutId=${layoutId}&relationIdentifier=${relationIdentifier}&unassigned=${unassigned}`
    // RUM 005: empty entityId/layoutId query params caused HTTP 400 on /entities
    if (!isValidEntityDataId(entityId) || !isValidEntityDataId(layoutId)) {
      return Promise.resolve(EMPTY_SUB_RESOURCE_PAGE)
    }
    validateApiParams(
      { resolvedEntityName, dataId, entityId, layoutId },
      {
        endpoint,
        method: 'GET',
      }
    )
    return await axiosInstance.get(endpoint)
  } else {
    const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${
      ENTITY_MANAGER_END_POINTS.ENTITY_DATA
    }/${resolvedEntityName}${
      dataId ? '/' + dataId : ''
    }/entities/paginated?entityId=${entityId}&layoutId=${layoutId}&relationIdentifier=${relationIdentifier}&unassigned=${unassigned}&page=${page}&size=${rowsPerPage}`
    if (!isValidEntityDataId(entityId) || !isValidEntityDataId(layoutId)) {
      return Promise.resolve(EMPTY_SUB_RESOURCE_PAGE)
    }
    validateApiParams(
      { resolvedEntityName, dataId, entityId, layoutId },
      {
        endpoint,
        method: 'GET',
      }
    )
    return await axiosInstance.get(endpoint)
  }
}

export const getClientIntegerationConfig = ({
  clientId,
  integerationType,
}: {
  clientId: number
  integerationType: IntegrationType
}) => {
  return axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/get/client_integration_config`,
    {
      entity_name: 'client_integration_config',
      filters: [
        {
          criteria: [
            {
              field_name: 'client_id',
              operation: 'EQUALS',
              values: [String(clientId)],
            },
            {
              field_name: 'integration_type',
              operation: 'EQUALS',
              values: [integerationType],
            },
          ],
        },
      ],
      select: ['client_id', 'integration_type', 'expiration_period'],
    }
  )
}

export const getSubResourcesWithoutLayout = async (payload: {
  entityName: string
  unassigned: boolean
  dataId: string
  page?: number
  rowsPerPage?: number
  filters?: { logical_operator?: string; criteria: formatDataObject[] }[]
  cols?: string[]
  related_entities?: Record<
    string,
    {
      select: string[]
    }
  >
}) => {
  const {
    dataId,
    entityName,
    unassigned,
    cols,
    filters,
    page,
    related_entities,
    rowsPerPage,
  } = payload
  if (!isValidEntityDataId(dataId)) {
    return Promise.resolve(EMPTY_SUB_RESOURCE_PAGE)
  }
  const resolvedEntityName = resolveEntityName(entityName, undefined)
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/get/${resolvedEntityName}/${dataId}/entities?unassigned=${unassigned}`
  validateApiParams(
    { resolvedEntityName, dataId },
    {
      endpoint,
      method: 'POST',
    }
  )
  return await axiosInstance.post(endpoint, {
    entity_name: entityName,
    unassigned,
    page,
    size: rowsPerPage,
    filters,
    select: cols ?? [],
    related_entities: related_entities,
  })
}

export const createSubResource = async (
  entityName: string,
  entityId: string,
  layoutId: string,
  dataId: string,
  relationIdentifier: string,
  values: DataTypeStore,
  missingMiIdentifier?: boolean,
  caseId?: string,
  state?: string
) => {
  const resolvedEntityName = resolveEntityName(entityName, entityId)
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/entities/${dataId}/${resolvedEntityName}`
  validateApiParams(
    { resolvedEntityName, dataId },
    {
      endpoint,
      method: 'PUT',
    }
  )

  const formattedValues = Object.fromEntries(
    Object.entries(values).map(([key, val]) => {
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        return [key, { ...(val as object), _state: 'ADDED' }]
      }
      return [key, val]
    })
  )

  return await axiosInstance.put(endpoint, {
    meta_data: {
      entity_id: entityId,
      layout_id: layoutId,
      relation_identifier: relationIdentifier,
    },
    data: {
      ...formattedValues,
      ...(missingMiIdentifier && { case_id: caseId }),
      ...(state && { _state: state }),
    },
  })
}

export const updateSubResource = async (
  entityId: string,
  layoutId: string,
  dataId: string,
  values: { [key: string]: unknown } | undefined,
  state?: string
) => {
  const resolvedEntityName = getEntityNameById(entityId)
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${resolvedEntityName}/${dataId}`
  validateApiParams(
    { resolvedEntityName, dataId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(endpoint, {
    meta_data: {
      entity_id: entityId,
      layout_id: layoutId,
    },
    data: {
      ...values,
      id: dataId,
      ...(state ? { _state: state } : {}),
    },
  })
}

export const updateSubResource360 = async (
  entityId: string,
  keyName: string,
  dataId: string,
  values: { [key: string]: unknown } | undefined
) => {
  const resolvedEntityName = getEntityNameById(entityId)
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${resolvedEntityName}/${dataId}`
  validateApiParams(
    { resolvedEntityName, dataId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(endpoint, {
    meta_data: {
      entity_id: entityId,
      embedded: [keyName, 'id'],
    },
    data: {
      ...values,
      id: dataId,
    },
  })
}

// Not using this api as of now as it is not deployed
export const updateSubResource360v1 = async (
  entityId: string,
  relationIdentifier: string,
  values: DataTypeStore[] | undefined,
  dataId: string,
  parentEntity: string,
  childEntity: string
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${parentEntity}/${dataId}/${childEntity}/assignment?entityId=${entityId}&relationIdentifier=${relationIdentifier}`
  validateApiParams(
    { parentEntity, dataId, childEntity },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(endpoint, values)
}

export const getRelatedFields = async (payload: {
  entityId: string
  singleLookup: boolean
  multiLookup: boolean
  isGlobal?: boolean
}) => {
  if (payload?.isGlobal === undefined) {
    const endpoint = `${API_END_POINT.ENTITY_MANAGER}entities/${payload?.entityId}?singleLookup=${payload?.singleLookup}&multiLookup=${payload?.multiLookup}`
    validateApiParams(
      { entityId: payload?.entityId },
      {
        endpoint,
        method: 'GET',
      }
    )
    return axiosInstance.get(endpoint)
  } else {
    const endpoint = `${API_END_POINT.ENTITY_MANAGER}entities/${payload?.entityId}?singleLookup=${payload?.singleLookup}&multiLookup=${payload?.multiLookup}&isGlobal=${payload?.isGlobal}`
    validateApiParams(
      { entityId: payload?.entityId },
      {
        endpoint,
        method: 'GET',
      }
    )
    return axiosInstance.get(endpoint)
  }
}

export const getSourceTypes = async () => {
  return axiosInstance.get(
    `${API_END_POINT?.ENTITY_MANAGER}entities/source_type`
  )
}

export const toggleAuditHistoryButtonAPI = async (payload: {
  data: { agent_visibility: boolean; enable_audit: boolean }
  id: string
}) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER}entities/${payload?.id}`
  validateApiParams(
    { id: payload?.id },
    {
      endpoint,
      method: 'PATCH',
    }
  )
  return await axiosInstance.patch(endpoint, payload?.data)
}

export const getLayoutTree = async (
  entityId: string,
  treeDepth: string,
  exclusionList?: string[]
) => {
  const endpoint = `${
    API_END_POINT?.ENTITY_MANAGER
  }entities/tree?entityId=${entityId}&treeDepth=${treeDepth}&exclusionList=${exclusionList?.join(
    ','
  )}`
  validateApiParams(
    { entityId },
    {
      endpoint,
      method: 'GET',
    }
  )
  return axiosInstance.get(endpoint)
}

export const postEntityPermissions = async (payload: EntityPermissionType) => {
  return await axiosInstance.post(
    `${API_END_POINT.V0_ENTITY_MANAGER}/permission`,
    payload
  )
}

export const getEntityPermissions = async (
  roleId: number,
  entityId: number
) => {
  const endpoint = `${API_END_POINT.V0_ENTITY_MANAGER}/permission?roleId=${roleId}&entityId=${entityId}`
  validateApiParams(
    { roleId, entityId },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint)
}

export const getEntityDataByEntityNameIdApiForFilter = async (
  action: GetEntityDataByNameIdFilter,
  recordId: string
) => {
  const resolvedEntityName = resolveEntityName(
    action.payload.entity_name,
    undefined
  )
  validateApiParams(
    { entity_name: resolvedEntityName, recordId },
    {
      endpoint: `${ENTITY_ENDPOINT}/get/${resolvedEntityName}/${recordId}`,
      method: 'POST',
    }
  )
  return await axiosInstance.post(
    `${ENTITY_ENDPOINT}/get/${resolvedEntityName}/${recordId}`,
    { ...action.payload, entity_name: resolvedEntityName }
  )
}

export const exportEntityDataByEntityIdLayoutIdApiForFilter = async (
  payload: ExportPayload
) => {
  const resolvedEntityName = resolveEntityName(payload.entity_name, undefined)
  const endpoint = `${ENTITY_ENDPOINT}/export/${resolvedEntityName}`
  validateApiParams(
    { entity_name: resolvedEntityName },
    { endpoint, method: 'POST' }
  )
  return await axiosInstance.post(endpoint, {
    ...payload,
    entity_name: resolvedEntityName,
  })
}

export const exportEntity2ChildData = async (payload: ExportPayload) => {
  const resolvedEntityName = resolveEntityName(payload.entity_name, undefined)
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/export/${resolvedEntityName}`
  validateApiParams(
    { entity_name: resolvedEntityName },
    { endpoint, method: 'POST' }
  )
  return await axiosInstance.post(endpoint, {
    ...payload,
    entity_name: resolvedEntityName,
  })
}

export const getEntityDataByEntityIdLayoutIdApiForFilter = async (
  action: getEntityDataByEntityFilter
) => {
  const resolvedEntityName = resolveEntityName(
    action?.entityName,
    action?.payload?.entity_id
  )
  const endpoint = `${ENTITY_ENDPOINT}/get/${resolvedEntityName}`
  validateApiParams(
    { entityName: resolvedEntityName },
    {
      endpoint,
      method: 'POST',
    }
  )
  return await axiosInstance.post(endpoint, action?.payload)
}

export const getEntityDataByEntityIdApiForFilter = async (
  action: getEntityDataByEntityFilter
) => {
  const resolvedEntityName = resolveEntityName(
    action?.entityName,
    action?.payload?.entity_id
  )
  const endpoint = `${API_END_POINT?.ENTITY_MANAGER_V3}entity-data/filters/${resolvedEntityName}`
  validateApiParams(
    { entityName: resolvedEntityName },
    {
      endpoint,
      method: 'POST',
    }
  )
  return await axiosInstance.post(endpoint, {
    ...action?.payload,
    entity_name: resolvedEntityName,
  })
}

export const getEntityDataByEntityNameApiForFilter = async (
  action: GetEntityDataByNameIdFilter
) => {
  const resolvedEntityName = resolveEntityName(
    action.payload.entity_name,
    undefined
  )
  const endpoint = `${ENTITY_ENDPOINT}/get/${resolvedEntityName}`
  validateApiParams(
    { entity_name: resolvedEntityName },
    {
      endpoint,
      method: 'POST',
    }
  )
  return await axiosInstance.post(endpoint, {
    ...action.payload,
    entity_name: resolvedEntityName,
  })
}

export const getCategoryDropdownOptions = async (
  categoryKey: string,
  select?: string[]
) => {
  const payload = {
    entity_name: 'category_values',
    select: select ?? ['name', 'value'],

    filters: [
      {
        criteria: [
          {
            field_name: 'category',
            operation: 'EQUALS',
            values: [categoryKey],
          },
        ],
      },
    ],
  }

  return await axiosInstance.post(
    `${ENTITY_ENDPOINT}/get/category_values`,
    payload
  )
}

export const saveCopayConfiguration = async (
  payload: HCPCopayConfigurationResponse,
  portalId: string
) => {
  return await axiosInstance.put(
    `${API_END_POINT.CORE}hcp/microsite/econfig/v3/portals/${portalId}/copay-configuration`,
    payload
  )
}

export const getCopayConfiguration = async (portalId: string) => {
  return await axiosInstance.get(
    `${API_END_POINT.CORE}hcp/microsite/econfig/v3/portals/${portalId}/copay-configuration`
  )
}

export const getCopayPdfPresignedUrl = async (
  portalId: string,
  params: { alias: string; size: number; contentType: string }
) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}hcp/microsite/econfig/v3/copay-pdf/${portalId}`,
    null,
    { params }
  )
}

export const getTaggedCopayPdf = async (documentId: number) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}hcp/microsite/econfig/v3/copay-pdf/${documentId}/tagged`,
    null
  )
}

export const getAllCategoryValues = async () => {
  return await axiosInstance.get(
    `${API_END_POINT.ENTITY_MANAGER_V2}category-value`
  )
}

//case type to layout mapping
export const postServiceBasedLayoutRecords = async (
  payload: CaseTypeServiceMappingPayload[]
) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER_V3}layout/case-type/layouts`,
    payload
  )
}

export const editServiceBasedLayoutRecords = async (
  payload: CaseTypeServiceMappingPayload[]
) => {
  return await axiosInstance.put(
    `${API_END_POINT.ENTITY_MANAGER_V3}layout/case-type/edit/layouts`,
    payload
  )
}

export const getAllServiceBasedLayoutRecords = async () => {
  return await axiosInstance.get(
    `${API_END_POINT.ENTITY_MANAGER_V3}layout/case-type/layouts/all`
  )
}

export const getAllLayoutRecordsBasedOnService = async (serviceId: number) => {
  return await axiosInstance.get(
    `${API_END_POINT.ENTITY_MANAGER_V3}layout/case-type/layouts/fetch/${serviceId}`
  )
}

export const getCaseDetailsFromCaseId = async (
  caseId: number,
  payload: ObjectType
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/case/${caseId}`
  validateApiParams(
    { caseId },
    {
      endpoint,
      method: 'POST',
    }
  )
  return await axiosInstance.post(endpoint, payload)
}

export const getEntityList = async (payload: {
  entity_name: string
  embedded_name: string
}) => {
  const resolvedEntityName = resolveEntityName(payload.entity_name, undefined)
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/${resolvedEntityName}/paginated?embedded=${payload.embedded_name}&size=10000&entityName=${resolvedEntityName}`
  validateApiParams(
    { entity_name: resolvedEntityName },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint)
}

export const fetchProductListAPI = async (id: number) => {
  const endpoint = `${API_END_POINT.CORE}programs/${id}/products`
  validateApiParams(
    { id },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint)
}

export const fetchServiceListAPI = async (
  programId: string,
  productId: string
) => {
  const endpoint = `${API_END_POINT.CORE}programs/${programId}/products/${productId}/services`
  validateApiParams(
    { programId, productId },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint)
}

export const fetchWorkflowDefinitionsAPI = async (
  searchCriteria: Array<{
    search_key: string
    search_value: string[]
    operator: string
  }>
) => {
  return await axiosInstance.post(
    `${API_END_POINT?.WORKFLOW}/process/get/definitions?page=0&size=100`,
    {
      search_criteria: searchCriteria,
    }
  )
}

export const fetchWorkflowDefinitionsAPIByProgramService = async (
  searchCriteria: Array<{
    search_key: string
    search_value: string[]
    operator: string
  }>
) => {
  return await axiosInstance.post(
    `${API_END_POINT?.WORKFLOW}/process/get/definitions/by-program-service?page=0&size=100`,
    {
      search_criteria: searchCriteria,
    }
  )
}

export const fetchDuplicateRecordsAPI = async (
  entityId: string | number,
  payload: {
    entity_id: string
    layout_id: string
    filters: EntityLookupSearchCriteria[]
    select: string[]
    related_entities: {
      [key: string]: {
        select: string[]
        filters: EntityLookupSearchCriteria[]
      }
    }
  },
  page = 0,
  size = 25
) => {
  const endpoint = `${API_END_POINT?.DUPLICATION}${entityId}?page=${page}&size=${size}`
  validateApiParams(
    { entityId },
    {
      endpoint,
      method: 'POST',
    }
  )
  return await axiosInstance.post(endpoint, payload)
}

export const fetchDuplicateManagementData = async (
  entityId: number,
  isCaseEntity = false
) => {
  const url = `${API_END_POINT.ENTITY_MANAGER}de-duplication/${entityId}${
    isCaseEntity ? '/bulk' : ''
  }`
  validateApiParams(
    { entityId },
    {
      endpoint: url,
      method: 'GET',
    }
  )
  return await axiosInstance.get(url)
}

export const updateDuplicateManagementData = async (
  entityId: number,
  payload: DuplicateFormValues | SavedConfigTypeAPI[],
  isCaseEntity = false
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER}de-duplication/${entityId}${
    isCaseEntity ? '/bulk' : ''
  }`
  validateApiParams(
    { entityId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(endpoint, payload)
}

export const getDuplicateFilterDropdownValues = async (
  entityId: string | number
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER}de-duplication/${entityId}/filter-dropdown-values`
  validateApiParams(
    { entityId },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint)
}

export const associateExistingCaseData = async (payload: {
  existingCaseId: number
  newCaseId: number | null
  requestorId: number | null
  schemaName: number | string
}) => {
  const url = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/procedure/${payload.schemaName}`
  const data = {
    params: {
      p_existing_case_id: {
        type: 'LONGDATATYPE',
        order: 1,
        value: payload.existingCaseId,
        is_input_param: true,
      },
      p_new_case_id: {
        type: 'LONGDATATYPE',
        order: 2,
        value: payload.newCaseId || null,
        is_input_param: true,
      },
      p_requestor_id: {
        type: 'LONGDATATYPE',
        order: 3,
        value: payload.requestorId,
        is_input_param: true,
      },
      p_result_case_id: {
        type: 'LONGDATATYPE',
        order: 5,
        is_input_param: false,
      },
    },
    procedureName: 'existing_case_data_association',
    primary_key_name: 'p_result_case_id',
  }

  return await axiosInstance.post(url, data)
}

export const procedureCallCorrespondingToTenant = async (
  tenantId: string,
  payload: FieldValues
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/procedure/${tenantId}`

  validateApiParams(
    { tenantId },
    {
      endpoint,
      method: 'POST',
    }
  )

  return await axiosInstance.post(endpoint, payload)
}

export const fetchDuplicateRecordsForStagingAPI = async (
  entityId: string | number,
  payload: {
    entity_id: string
    layout_id: string
    case_key?: string
    filters: EntityLookupSearchCriteria[]
    select: string[]
    related_entities: {
      [key: string]: {
        select: string[]
        filters: EntityLookupSearchCriteria[]
      }
    }
  }[],
  page = 0,
  size = 25
) => {
  const endpoint = `${API_END_POINT?.DUPLICATION}${entityId}/bulk?page=${page}&size=${size}`
  validateApiParams(
    { entityId },
    {
      endpoint,
      method: 'POST',
    }
  )
  return await axiosInstance.post(endpoint, payload)
}
